import { React, useEffect, useRef, useState } from 'react'
import { Card } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { colors } from 'globalStyles'
import styled from 'styled-components'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as Pencil } from 'assets/svg/pencil.svg'
import { ReactComponent as HouseBuilding } from 'assets/svg/house-building-fa.svg'
import { ReactComponent as ChevronDown } from 'assets/svg/chevron-down.svg'
import PropTypes from 'prop-types'
import { getUserPreferencesInfo } from 'store/recipient/recipientSlice'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import SuccessModal from 'components/SuccessModal'
import { updateUserPreferences } from 'api/accounts'
import { locations, businessLocations } from 'components/DropOffLocation/config'
import CheckMark from 'reusableComponents/CheckMark'
import {
  pencilIconStyle,
  tableHeaderStyle,
  tableColumnStyle,
  tableContentStyle,
  tableSubContentStyle,
  LocationStyledCard,
  houseBuildingIconStyle,
  CenteredDiv,
} from './MyPreferences.style'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

const LOCATION_DROPDOWN_ORDER = [
  'front_door',
  'rear_door',
  'leave_with_concierge_front_desk',
  'basement_side_door',
  'porch',
  'garage',
  'shipping_dock',
  'reception',
  'mailroom',
]

const LocationSelectContainer = styled.div`
  .location-dropdown {
    position: relative;
    width: 100%;
    max-width: 100%;
  }

  .location-dropdown-trigger {
    width: 100%;
    border: 1px solid #ced4da;
    border-radius: 0.375rem;
    background: ${colors.primary_white};
    text-align: left;
    min-height: 58px;
    padding: 8px 36px 8px 12px;
    position: relative;
    display: block;
  }

  .location-dropdown-trigger.open {
    border-color: ${colors.primary_blue};
    box-shadow: 0 0 0 2px rgba(0, 25, 150, 0.15);
  }

  .location-dropdown-label {
    display: block;
    font-size: 14px;
    color: ${colors.secondary_grey};
    line-height: 18px;
  }

  .location-dropdown-value {
    display: block;
    font-size: 16px;
    color: ${colors.web_text_black};
    line-height: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .location-dropdown-chevron {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.secondary_grey};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .location-dropdown-chevron svg {
    transition: transform 0.2s ease;
    transform: rotate(0deg);
  }

  .location-dropdown-chevron.open svg {
    transform: rotate(180deg);
  }

  .location-dropdown-menu {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    width: 100%;
    max-height: 260px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
    background: ${colors.primary_white};
    border: 1px solid #ced4da;
    border-radius: 0 0 0.375rem 0.375rem;
    z-index: 2000;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  }

  .location-dropdown-option {
    width: 100%;
    border: 0;
    background: ${colors.primary_white};
    text-align: left;
    padding: 10px 12px;
    color: ${colors.web_text_black};
    line-height: 24px;
  }

  .location-dropdown-option:hover {
    background: ${colors.dropdown_hover_light_blue};
  }

  .location-dropdown-option.selected {
    background: ${colors.primary_blue};
    color: ${colors.primary_white};
  }
`

const LocationPreference = ({ userPreferenceDetails }) => {
  const prn = pywBackendURL
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const dispatch = useDispatch()
  const { language } = i18n
  const [disableSaveButton, setDisableSaveButton] = useState(true)
  const [isEditingLocationPreference, setIsEditingLocationPreference] = useState(false)
  const [showGreenCheck, setShowGreenCheck] = useState(false)
  const [deliveryLocationPreference, setDeliveryLocationPreference] = useState(
    userPreferenceDetails.deliveryLocation
  )
  const [saveLocationPreference, setSaveLocationPreference] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false)
  const locationDropdownRef = useRef(null)
  const translationKey = 'deliveryPreferencesSummary.delivery_opt'

  const deliveryLocation = deliveryLocationPreference
    ? t(`${translationKey}.${deliveryLocationPreference}`)
    : t(`my_preferences.none_selected`)

  const dropOffLocations = [...locations, ...businessLocations]
    .filter((location) => location.value !== 'business_location_mailroom')
    .sort(
      (leftLocation, rightLocation) =>
        LOCATION_DROPDOWN_ORDER.indexOf(leftLocation.value) -
        LOCATION_DROPDOWN_ORDER.indexOf(rightLocation.value)
    )
    .map((location) => ({
      value: location.value,
      label: t(location.label),
    }))
  const cancelModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
    if (saveLocationPreference) dispatch(getUserPreferencesInfo(prn))
  }, [lang, i18n, language, saveLocationPreference, dispatch, prn])

  const editLocationPreference = () => {
    setIsEditingLocationPreference(true)
  }

  const selectedDropOffLocationLabel =
    dropOffLocations.find((location) => location.value === deliveryLocationPreference)?.label ||
    t(`my_preferences.select_loc`)

  const dropOffLocChangeHandler = (selectedDropOffLocation) => {
    setDeliveryLocationPreference(selectedDropOffLocation)
    setDisableSaveButton(false)
    setIsLocationMenuOpen(false)
  }

  const toggleDropOffMenu = () => {
    setIsLocationMenuOpen((prevState) => !prevState)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const message = t('my_preferences.successfully_updated')
    try {
      await updateUserPreferences('deliveryLocation', deliveryLocationPreference)
    } catch {
      setSaveLocationPreference(false)
    } finally {
      setSaveLocationPreference(true)
      setSuccessMessage(message)
      setShowModal(true)
      setTimeout(() => {
        setShowModal(false)
        setShowGreenCheck(true)
      }, 3000)

      setIsEditingLocationPreference(false)
      setDisableSaveButton(true)
    }
  }
  const handleCheckMarkComplete = () => {
    setShowGreenCheck(false)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setIsLocationMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <LocationStyledCard style={{ marginTop: '24px' }}>
      <Card.Header
        style={{
          backgroundColor: isEditingLocationPreference ? `${colors.sky_blue}` : 'inherit',
          borderRadius: isEditingLocationPreference ? '8px 0 0 0' : '8px 0 8px 0',
          padding: '12px',
        }}
      >
        <div style={{ display: 'table', width: '100%' }}>
          <div style={tableHeaderStyle}>
            <SVGIcon
              Icon={HouseBuilding}
              size="lg"
              color={colors.secondary_light_blue}
              aria-label="Location preference"
              style={houseBuildingIconStyle}
            />
          </div>
          <div style={{ display: 'table-cell' }}>
            <div style={tableContentStyle}>{t(`my_preferences.delivery_location`)}</div>
            <div style={tableSubContentStyle}>{deliveryLocation}</div>
          </div>
          <div style={tableColumnStyle}>
            {!isEditingLocationPreference && !showGreenCheck && (
              <SVGIcon
                Icon={Pencil}
                size="lg"
                color={colors.primary_blue}
                aria-label="Edit location preference"
                onClick={editLocationPreference}
                style={pencilIconStyle}
              />
            )}
            {!isEditingLocationPreference && showGreenCheck && (
              <CheckMark
                color={colors.primary_green}
                size="lg"
                delay="10000"
                onComplete={handleCheckMarkComplete}
              />
            )}
          </div>
        </div>
      </Card.Header>
      <Form id="saveLocationPreferenceForm" onSubmit={handleSubmit}>
        {isEditingLocationPreference && (
          <>
            <Card.Body>
              <Card.Title>{t(`my_preferences.delivery_loc`)}</Card.Title>
              <Card.Text>{t(`my_preferences.drop_off_msg`)}</Card.Text>
              <LocationSelectContainer>
                <div ref={locationDropdownRef} className="location-dropdown">
                  <button
                    type="button"
                    className={`location-dropdown-trigger ${isLocationMenuOpen ? 'open' : ''}`}
                    aria-haspopup="listbox"
                    aria-expanded={isLocationMenuOpen}
                    aria-label={t(`my_preferences.select_loc`)}
                    onClick={toggleDropOffMenu}
                  >
                    <span className="location-dropdown-label">
                      {t(`my_preferences.select_loc`)}
                    </span>
                    <span className="location-dropdown-value">{selectedDropOffLocationLabel}</span>
                    <span
                      className={`location-dropdown-chevron ${isLocationMenuOpen ? 'open' : ''}`}
                    >
                      <SVGIcon Icon={ChevronDown} size="xs" color={colors.secondary_grey} />
                    </span>
                  </button>
                  {isLocationMenuOpen && (
                    <ul className="location-dropdown-menu">
                      {dropOffLocations.map((location) => (
                        <li key={location.value}>
                          <button
                            type="button"
                            className={`location-dropdown-option ${
                              location.value === deliveryLocationPreference ? 'selected' : ''
                            }`}
                            onClick={() => dropOffLocChangeHandler(location.value)}
                          >
                            {location.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </LocationSelectContainer>
            </Card.Body>
            <CenteredDiv>
              <Button
                type="submit"
                style={{ width: '98%' }}
                disabled={disableSaveButton}
                form="saveLocationPreferenceForm"
              >
                {t('buttonActions.save')}
              </Button>
            </CenteredDiv>
          </>
        )}
        <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      </Form>
    </LocationStyledCard>
  )
}

LocationPreference.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userPreferenceDetails: PropTypes.object.isRequired,
}

export default LocationPreference
